// Phase 10B "AutoMind 2050" — Voice Library.
//
// Voice samples available to the AI agent. All existing fetch / play logic
// preserved; only JSX + classNames changed.

import React, { useState, useEffect } from 'react';
import { Play, Check, RefreshCw } from 'lucide-react';

import { PageHeader } from '../components/ui/page-header';
import { TabBar } from '../components/ui/tab-bar';
import { DataCard } from '../components/ui/data-card';
import { SectionGrid } from '../components/ui/section-grid';
import { EmptyState } from '../components/ui/empty-state';
import { StatusPill } from '../components/ui/status-pill';

const FEMALE_NAMES = ['Sarah', 'Laura', 'Emily', 'Charlotte'];
const MALE_NAMES = ['Charlie', 'Liam', 'Daniel', 'Adam'];

const VoiceLibrary = () => {
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    fetchVoices();
  }, []);

  const fetchVoices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voices`);
      const data = await response.json();
      setVoices(data.voices || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching voices:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchVoices();
  };

  const handleTestVoice = async (voiceId, voiceName) => {
    try {
      const startTime = Date.now();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/voices/test-preview?voice_id=${voiceId}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) {
        throw new Error(`Failed to generate preview: ${response.status} ${response.statusText}`);
      }
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const latency = Date.now() - startTime;
      const audio = new Audio(audioUrl);
      audio.play();
      // eslint-disable-next-line no-alert
      alert(`Voice Preview: ${voiceName}\n\nLatency: ${latency}ms\n\nPlaying audio sample...`);
      audio.onended = () => URL.revokeObjectURL(audioUrl);
    } catch (err) {
      console.error('Error testing voice:', err);
      // eslint-disable-next-line no-alert
      alert(`Failed to test voice: ${err.message}\n\nVoice preview is managed in the Retell dashboard.`);
    }
  };

  const filteredVoices = voices.filter((v) => {
    if (tab === 'all') return true;
    if (tab === 'female') return FEMALE_NAMES.includes(v.name);
    if (tab === 'male') return MALE_NAMES.includes(v.name);
    if (tab === 'cloned') return v.category === 'cloned';
    return true;
  });

  const tabs = [
    { id: 'all', label: 'All', badge: voices.length || undefined },
    { id: 'female', label: 'Female' },
    { id: 'male', label: 'Male' },
    { id: 'cloned', label: 'Cloned' },
  ];

  return (
    <div className="space-y-6" data-testid="voice-library-page">
      <PageHeader
        eyebrow="AI"
        title="Voice Library"
        description="Voice samples available to the AI agent."
        actions={
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-2050 btn-secondary"
            data-testid="refresh-voices-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      <TabBar value={tab} onChange={setTab} items={tabs} />

      {error ? (
        <DataCard>
          <EmptyState
            title="Unable to load right now."
            description={error}
          >
            <button
              type="button"
              onClick={fetchVoices}
              className="btn-2050 btn-secondary"
              data-testid="retry-voices-button"
            >
              Retry
            </button>
          </EmptyState>
        </DataCard>
      ) : loading ? (
        <SectionGrid cols={3}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <DataCard key={i} loading />
          ))}
        </SectionGrid>
      ) : filteredVoices.length === 0 ? (
        <DataCard>
          <EmptyState
            title="No voices available"
            description="Voices are configured on the Retell agent. Add a voice in the Retell dashboard to see it here."
          />
        </DataCard>
      ) : (
        <SectionGrid cols={3} data-testid="voice-library-grid">
          {filteredVoices.map((voice) => {
            const isSelected = selectedVoice === voice.voice_id;
            return (
              <DataCard
                key={voice.voice_id}
                data-testid={`voice-card-${voice.voice_id}`}
                title={<span className="capitalize">{voice.name}</span>}
                description={voice.category ? `${voice.category} voice` : 'Voice'}
                actions={
                  isSelected ? <StatusPill tone="gold" label="Selected" /> : null
                }
              >
                <div className="space-y-4">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500">
                    Voice ID
                  </div>
                  <div className="text-xs font-mono text-ink-700 dark:text-ink-300 truncate">
                    {voice.voice_id}
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-ink-200/60 dark:border-ink-800/60">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
                        Language
                      </p>
                      <p className="text-sm font-medium">{voice.language || 'en-US'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">
                        Sample
                      </p>
                      <p className="text-sm font-medium">~12s</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => handleTestVoice(voice.voice_id, voice.name)}
                      className="btn-2050 btn-secondary flex-1"
                      data-testid={`test-voice-${voice.voice_id}`}
                      data-voice-id={voice.voice_id}
                    >
                      <Play className="h-3.5 w-3.5" />
                      Play sample
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedVoice(voice.voice_id)}
                      className={isSelected ? 'btn-2050 btn-primary' : 'btn-2050 btn-ghost'}
                      data-testid={`select-voice-${voice.voice_id}`}
                    >
                      {isSelected ? <Check className="h-3.5 w-3.5" /> : 'Test'}
                    </button>
                  </div>
                </div>
              </DataCard>
            );
          })}
        </SectionGrid>
      )}

      {selectedVoice && (
        <DataCard
          title="Currently selected voice"
          actions={
            <button type="button" className="btn-2050 btn-primary" data-testid="save-default-voice-btn">
              Save as default
            </button>
          }
        >
          <p className="font-medium capitalize">
            {voices.find((v) => v.voice_id === selectedVoice)?.name || 'None'}
          </p>
        </DataCard>
      )}
    </div>
  );
};

export default VoiceLibrary;
